/** Tradução do modelo do miolo (papéis de cor) para CSS. */

import type { ConfigTipografia, CorPapel, NomeFonte, Paleta, Preenchimento, TipoBorda } from '@miolos/core';

export function corDoPapel(paleta: Paleta, papel: CorPapel): string {
  switch (papel) {
    case 'TEXTO':
      return paleta.texto;
    case 'TEXTO_SUAVE':
      return paleta.textoSuave;
    case 'PRINCIPAL':
      return paleta.principal;
    case 'PRINCIPAL_ESCURO':
      return paleta.principalEscuro;
    case 'PRINCIPAL_CLARO':
      return paleta.principalClaro;
    case 'DESTAQUE':
      return paleta.destaque;
    case 'DESTAQUE_TEXTO':
      return paleta.destaqueTexto;
    case 'DESTAQUE_SUAVE':
      return paleta.destaqueSuave;
    case 'INVERSO':
      return paleta.inverso;
    case 'LINHA':
    default:
      return paleta.linha;
  }
}

export function fundoDoPreenchimento(paleta: Paleta, tipo: Preenchimento): string {
  switch (tipo) {
    case 'SUAVE':
      return paleta.fundoAlt;
    case 'PRINCIPAL':
      return paleta.principal;
    case 'DESTAQUE':
      return paleta.destaque;
    case 'DESTAQUE_SUAVE':
      return paleta.destaqueSuave;
    case 'INVERSO':
      return paleta.principalEscuro;
    case 'NENHUM':
    default:
      return 'transparent';
  }
}

export type EstiloBordaCss = 'solid' | 'dashed' | 'dotted' | 'none';

export function bordaDoTipo(
  paleta: Paleta,
  tipo: TipoBorda,
): { borderWidth: string; borderStyle: EstiloBordaCss; borderColor: string } {
  switch (tipo) {
    case 'SUAVE':
      return { borderWidth: '0.25mm', borderStyle: 'solid', borderColor: paleta.borda };
    case 'PRINCIPAL':
      return { borderWidth: '0.4mm', borderStyle: 'solid', borderColor: paleta.principal };
    case 'DESTAQUE':
      return { borderWidth: '0.4mm', borderStyle: 'solid', borderColor: paleta.destaque };
    case 'TRACEJADA':
      return { borderWidth: '0.25mm', borderStyle: 'dashed', borderColor: paleta.linha };
    case 'INVERSO':
      return { borderWidth: '0.4mm', borderStyle: 'solid', borderColor: `${paleta.inverso}88` };
    case 'NENHUMA':
    default:
      return { borderWidth: '0', borderStyle: 'none', borderColor: 'transparent' };
  }
}

/** Tamanho em pt do símbolo do mini calendário, usando container queries. */
export const TAMANHO_MINI = { dia: '8.4cqw', cabecalho: '6.6cqw', titulo: '10cqw' } as const;

export interface Fontes {
  titulo: string;
  corpo: string;
}

/**
 * Pilha de fontes do desenho, a partir da tipografia escolhida.
 *
 * A **família** é a base: decide serifa, sem serifa, monoespaçada ou mista.
 * A **fonte pelo nome** entra na frente dela, e a família fica como reserva — é
 * por isso que a pilha do sistema não cita 'Inter' como se ela já estivesse
 * instalada: quem não escolheu fonte nomeada continua com a letra do aparelho,
 * exatamente como antes. A fonte nomeada é servida pelo próprio site
 * (`apps/web/public/fontes/`), então ela carrega também no Chromium do servidor
 * e sai embutida no PDF.
 */
export function fontesDe(tipografia: ConfigTipografia): Fontes {
  const serifa = "Georgia, 'Iowan Old Style', 'Times New Roman', Times, serif";
  const sans = "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
  const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

  let base: Fontes;
  switch (tipografia.familia) {
    case 'SERIFA':
      base = { titulo: serifa, corpo: serifa };
      break;
    case 'SANS':
      base = { titulo: sans, corpo: sans };
      break;
    case 'MONO':
      base = { titulo: mono, corpo: mono };
      break;
    case 'MISTA':
    default:
      base = { titulo: serifa, corpo: sans };
      break;
  }

  return {
    titulo: comFonteNomeada(base.titulo, tipografia.fonteTitulo),
    corpo: comFonteNomeada(base.corpo, tipografia.fonteTexto),
  };
}

/** A fonte escolhida pelo nome, com a pilha da família logo atrás dela. */
function comFonteNomeada(pilha: string, nome: NomeFonte | undefined): string {
  if (!nome || nome === 'SISTEMA') return pilha;
  return `${FAMILIA_CSS[nome]}, ${pilha}`;
}

/** Nome da família em CSS — a mesma string do `@font-face` em `fontes.css`. */
const FAMILIA_CSS: Record<Exclude<NomeFonte, 'SISTEMA'>, string> = {
  PLAYFAIR_DISPLAY: "'Playfair Display'",
  INTER: "'Inter'",
  MONTSERRAT: "'Montserrat'",
  CAVEAT: "'Caveat'",
  DANCING_SCRIPT: "'Dancing Script'",
  GREAT_VIBES: "'Great Vibes'",
  NUNITO: "'Nunito'",
  QUICKSAND: "'Quicksand'",
  COMFORTAA: "'Comfortaa'",
  BALOO_2: "'Baloo 2'",
  POPPINS: "'Poppins'",
  OUTFIT: "'Outfit'",
  MANROPE: "'Manrope'",
  BARLOW_CONDENSED: "'Barlow Condensed'",
  OSWALD: "'Oswald'",
  PATRICK_HAND: "'Patrick Hand'",
  BEBAS_NEUE: "'Bebas Neue'",
};

/** A fonte escolhida, sozinha — para os nomes aparecerem na própria letra. */
export function familiaDaFonte(nome: NomeFonte): string {
  return nome === 'SISTEMA' ? 'inherit' : FAMILIA_CSS[nome];
}

export function pesoNumerico(peso: 'NORMAL' | 'MEDIO' | 'FORTE'): number {
  if (peso === 'FORTE') return 700;
  if (peso === 'MEDIO') return 550;
  return 400;
}

export function alinhamentoCss(alinhamento: 'ESQ' | 'CENTRO' | 'DIR'): 'left' | 'center' | 'right' {
  if (alinhamento === 'CENTRO') return 'center';
  if (alinhamento === 'DIR') return 'right';
  return 'left';
}
